import { useEffect, useState } from "react";
import { Form, Button, Row, Container, Col, Card } from "react-bootstrap";
import { Api } from "../../Helpers/axios/axios";
import { PageHeader } from "../../Component/Common/Headers";
import { LoadingSpinner } from "../../Component/Spinner/LoadingSpinner";
import { SeoFormSection } from "../../Component/Common/SeoFormSection";
import { FileUpload } from "../../Component/FileUpload/FileUpload";

export const HomePageForm = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>({
        hero: {
            title: { line1: '', highlightLine1: '', highlightLine2: '', line2: '' },
            subtitle: { line1: '', line2: '' },
            image: { src: '', alt: '' },
            search: { placeholder: '', quickTags: [] }
        },
        financialBanner: {
            titleLine1: '',
            titleLine2: '',
            description: '',
            features: [],
            scholarshipHighlight: { value: '', label: '' }
        },
        expertsSection: {
            badge: '',
            title: '',
            description: '',
            ctaText: '',
            ctaLink: '',
            consultBtnText: '',
            steps: [],
            experts: []
        },
        counsellingSection: {
            badge: '',
            title: '',
            description: '',
            points: [],
            guidance: { title: '', desc: '' },
            form: { badgeText: '', buttonText: '', titleLine1: '', titleHighlight: '', titleLine2: '' }
        },
        faqSection: {
            badge: '',
            titleLine1: '',
            titleLine2: '',
            counsellorCard: { text: '', buttonText: '', buttonLink: '' },
            faqs: []
        },
        callToAction: {
            logo: '',
            logoAlt: '',
            title: '',
            description: '',
            buttonText: '',
            buttonLink: ''
        },
        seoTitle: '',
        metaDescription: '',
        focusKeywords: '',
        schemaMarkup: '',
        isActive: true,
    });

    useEffect(() => {
        setLoading(true);
        Api.get("/home-page")
            .then((res: any) => {
                // Api.get already returns data directly through errorCheck(response)
                const resData = res?.data || res;
                if (resData && typeof resData === 'object') {
                    setData((prev: any) => ({
                        ...prev,
                        ...resData,
                        hero: { ...prev.hero, ...(resData.hero || {}) },
                        financialBanner: { ...prev.financialBanner, ...(resData.financialBanner || {}) },
                        expertsSection: { ...prev.expertsSection, ...(resData.expertsSection || {}) },
                        counsellingSection: { ...prev.counsellingSection, ...(resData.counsellingSection || {}) },
                        faqSection: { ...prev.faqSection, ...(resData.faqSection || {}) },
                        callToAction: { ...prev.callToAction, ...(resData.callToAction || {}) },
                    }));
                }
            })
            .catch((err: any) => {
                console.error("Error loading home page content:", err);
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await Api.post("/home-page", data);
            alert("Home page content saved successfully!");
        } catch (error) {
            console.error("Error saving home page:", error);
            alert("Failed to save home page content.");
        } finally {
            setLoading(false);
        }
    };

    const updateField = (path: string[], value: any) => {
        setData((prev: any) => {
            const next = { ...prev };
            let curr = next;
            for (let i = 0; i < path.length - 1; i++) {
                if (!curr[path[i]]) curr[path[i]] = {};
                curr[path[i]] = { ...curr[path[i]] };
                curr = curr[path[i]];
            }
            curr[path[path.length - 1]] = value;
            return next;
        });
    };

    if (loading) return <LoadingSpinner />;

    return (
        <Container fluid className="px-4 py-3">
            <PageHeader title="Home Page Management" />
            <Form onSubmit={handleSubmit}>
                {/* 1. Hero Section */}
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-light fw-bold">1. Hero Section</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Title Line 1</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.title?.line1 || ''}
                                    placeholder="There is an"
                                    onChange={(e) => updateField(['hero', 'title', 'line1'], e.target.value)}
                                />
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Highlight Line 1</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.title?.highlightLine1 || ''}
                                    placeholder="Online MBA for"
                                    onChange={(e) => updateField(['hero', 'title', 'highlightLine1'], e.target.value)}
                                />
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Highlight Line 2</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.title?.highlightLine2 || ''}
                                    placeholder="Where You"
                                    onChange={(e) => updateField(['hero', 'title', 'highlightLine2'], e.target.value)}
                                />
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Title Line 2</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.title?.line2 || ''}
                                    placeholder="Want to Go"
                                    onChange={(e) => updateField(['hero', 'title', 'line2'], e.target.value)}
                                />
                            </Col>

                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Subtitle Line 1</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.subtitle?.line1 || ''}
                                    onChange={(e) => updateField(['hero', 'subtitle', 'line1'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Subtitle Line 2</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.subtitle?.line2 || ''}
                                    onChange={(e) => updateField(['hero', 'subtitle', 'line2'], e.target.value)}
                                />
                            </Col>

                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Hero Banner Image</Form.Label>
                                <FileUpload
                                    fieldKey="heroImage"
                                    value={data.hero?.image?.src || ''}
                                    onChange={(_key: string, val: string) => updateField(['hero', 'image', 'src'], val)}
                                />
                                <Form.Control
                                    type="text"
                                    className="mt-2"
                                    placeholder="Or enter image URL manually..."
                                    value={data.hero?.image?.src || ''}
                                    onChange={(e) => updateField(['hero', 'image', 'src'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Image Alt Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.image?.alt || ''}
                                    placeholder="e.g. Woman with laptop"
                                    onChange={(e) => updateField(['hero', 'image', 'alt'], e.target.value)}
                                />
                            </Col>

                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Search Placeholder</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.hero?.search?.placeholder || ''}
                                    onChange={(e) => updateField(['hero', 'search', 'placeholder'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Quick Search Tags (comma-separated)</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={(data.hero?.search?.quickTags || []).join(', ')}
                                    placeholder="Data Science, Business Analytics, Digital Marketing"
                                    onChange={(e) => updateField(['hero', 'search', 'quickTags'], e.target.value.split(',').map((s: string) => s.trim()))}
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 2. Financial Assistance Banner */}
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-light fw-bold">2. Financial Assistance Banner</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Title Line 1</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.financialBanner?.titleLine1 || ''}
                                    onChange={(e) => updateField(['financialBanner', 'titleLine1'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Title Line 2</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.financialBanner?.titleLine2 || ''}
                                    onChange={(e) => updateField(['financialBanner', 'titleLine2'], e.target.value)}
                                />
                            </Col>
                            <Col md={12} className="mb-3">
                                <Form.Label className="generic-form-label">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={data.financialBanner?.description || ''}
                                    onChange={(e) => updateField(['financialBanner', 'description'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Features (comma-separated)</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={(data.financialBanner?.features || []).join(', ')}
                                    placeholder="University Scholarships, Flexible EMI Options"
                                    onChange={(e) => updateField(['financialBanner', 'features'], e.target.value.split(',').map((s: string) => s.trim()))}
                                />
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Scholarship Value</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.financialBanner?.scholarshipHighlight?.value || ''}
                                    placeholder="Up to 30%"
                                    onChange={(e) => updateField(['financialBanner', 'scholarshipHighlight', 'value'], e.target.value)}
                                />
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Scholarship Label</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.financialBanner?.scholarshipHighlight?.label || ''}
                                    placeholder="SCHOLARSHIP AVAILABLE"
                                    onChange={(e) => updateField(['financialBanner', 'scholarshipHighlight', 'label'], e.target.value)}
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 3. Experts Section */}
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-light fw-bold">3. Experts Section</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Section Badge</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.expertsSection?.badge || ''}
                                    placeholder="Course finder"
                                    onChange={(e) => updateField(['expertsSection', 'badge'], e.target.value)}
                                />
                            </Col>
                            <Col md={8} className="mb-3">
                                <Form.Label className="generic-form-label">Section Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.expertsSection?.title || ''}
                                    onChange={(e) => updateField(['expertsSection', 'title'], e.target.value)}
                                />
                            </Col>
                            <Col md={12} className="mb-3">
                                <Form.Label className="generic-form-label">Section Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={data.expertsSection?.description || ''}
                                    onChange={(e) => updateField(['expertsSection', 'description'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">CTA Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.expertsSection?.ctaText || ''}
                                    placeholder="Start my shortlisting process"
                                    onChange={(e) => updateField(['expertsSection', 'ctaText'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">CTA Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.expertsSection?.ctaLink || ''}
                                    placeholder="/#free-counselling"
                                    onChange={(e) => updateField(['expertsSection', 'ctaLink'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Consult Button Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.expertsSection?.consultBtnText || ''}
                                    placeholder="Consult Now"
                                    onChange={(e) => updateField(['expertsSection', 'consultBtnText'], e.target.value)}
                                />
                            </Col>

                            {/* Steps List */}
                            <Col md={12} className="mb-3 mt-2">
                                <Form.Label className="generic-form-label fw-bold">Steps</Form.Label>
                                {(data.expertsSection?.steps || []).map((step: any, index: number) => (
                                    <div key={index} className="p-3 mb-2 border rounded bg-light">
                                        <Row className="align-items-center">
                                            <Col md={2}>
                                                <Form.Label className="small text-muted mb-1">Step #</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={step.num || ''}
                                                    onChange={(e) => {
                                                        const steps = [...(data.expertsSection?.steps || [])];
                                                        steps[index].num = e.target.value;
                                                        updateField(['expertsSection', 'steps'], steps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Label className="small text-muted mb-1">Step Title</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={step.title || ''}
                                                    onChange={(e) => {
                                                        const steps = [...(data.expertsSection?.steps || [])];
                                                        steps[index].title = e.target.value;
                                                        updateField(['expertsSection', 'steps'], steps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={5}>
                                                <Form.Label className="small text-muted mb-1">Description</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={step.desc || ''}
                                                    onChange={(e) => {
                                                        const steps = [...(data.expertsSection?.steps || [])];
                                                        steps[index].desc = e.target.value;
                                                        updateField(['expertsSection', 'steps'], steps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={1} className="d-flex align-items-end pt-3">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        const steps = [...(data.expertsSection?.steps || [])];
                                                        steps.splice(index, 1);
                                                        updateField(['expertsSection', 'steps'], steps);
                                                    }}
                                                >
                                                    ✕
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        const steps = [...(data.expertsSection?.steps || [])];
                                        steps.push({ num: `0${steps.length + 1}`, title: '', desc: '' });
                                        updateField(['expertsSection', 'steps'], steps);
                                    }}
                                >
                                    + Add Step
                                </Button>
                            </Col>

                            {/* Experts List */}
                            <Col md={12} className="mb-3 mt-3">
                                <Form.Label className="generic-form-label fw-bold">Mentors / Experts</Form.Label>
                                {(data.expertsSection?.experts || []).map((exp: any, index: number) => (
                                    <div key={index} className="p-3 mb-3 border rounded bg-light">
                                        <Row className="align-items-center">
                                            <Col md={2}>
                                                <Form.Label className="small text-muted mb-1">Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={exp.name || ''}
                                                    onChange={(e) => {
                                                        const exps = [...(data.expertsSection?.experts || [])];
                                                        exps[index].name = e.target.value;
                                                        updateField(['expertsSection', 'experts'], exps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small text-muted mb-1">Rating</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={exp.rating || ''}
                                                    placeholder="4.8"
                                                    onChange={(e) => {
                                                        const exps = [...(data.expertsSection?.experts || [])];
                                                        exps[index].rating = e.target.value;
                                                        updateField(['expertsSection', 'experts'], exps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="small text-muted mb-1">Role</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={exp.role || ''}
                                                    placeholder="Sr. Mentor for MBA"
                                                    onChange={(e) => {
                                                        const exps = [...(data.expertsSection?.experts || [])];
                                                        exps[index].role = e.target.value;
                                                        updateField(['expertsSection', 'experts'], exps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small text-muted mb-1">Experience</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={exp.exp || ''}
                                                    placeholder="4 Years Experience"
                                                    onChange={(e) => {
                                                        const exps = [...(data.expertsSection?.experts || [])];
                                                        exps[index].exp = e.target.value;
                                                        updateField(['expertsSection', 'experts'], exps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small text-muted mb-1">Image Upload</Form.Label>
                                                <FileUpload
                                                    fieldKey={`expert-${index}`}
                                                    value={exp.image || ''}
                                                    onChange={(_key: string, val: string) => {
                                                        const exps = [...(data.expertsSection?.experts || [])];
                                                        exps[index].image = val;
                                                        updateField(['expertsSection', 'experts'], exps);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={1} className="d-flex align-items-end pt-3">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        const exps = [...(data.expertsSection?.experts || [])];
                                                        exps.splice(index, 1);
                                                        updateField(['expertsSection', 'experts'], exps);
                                                    }}
                                                >
                                                    ✕
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        const exps = [...(data.expertsSection?.experts || [])];
                                        exps.push({ id: exps.length + 1, name: '', rating: '4.8', role: '', exp: '', image: '' });
                                        updateField(['expertsSection', 'experts'], exps);
                                    }}
                                >
                                    + Add Mentor
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 4. Counselling Section */}
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-light fw-bold">4. Counselling Section</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Badge</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.counsellingSection?.badge || ''}
                                    placeholder="Free counselling"
                                    onChange={(e) => updateField(['counsellingSection', 'badge'], e.target.value)}
                                />
                            </Col>
                            <Col md={8} className="mb-3">
                                <Form.Label className="generic-form-label">Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.counsellingSection?.title || ''}
                                    placeholder="One call tells you everything."
                                    onChange={(e) => updateField(['counsellingSection', 'title'], e.target.value)}
                                />
                            </Col>
                            <Col md={12} className="mb-3">
                                <Form.Label className="generic-form-label">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={data.counsellingSection?.description || ''}
                                    onChange={(e) => updateField(['counsellingSection', 'description'], e.target.value)}
                                />
                            </Col>
                            <Col md={12} className="mb-3">
                                <Form.Label className="generic-form-label">Key Points (one per line)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={(data.counsellingSection?.points || []).join('\n')}
                                    placeholder="Always 100% free, no hidden charges&#10;CVCS-verified Advisors assigned"
                                    onChange={(e) => updateField(['counsellingSection', 'points'], e.target.value.split('\n').filter(Boolean))}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Guidance Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.counsellingSection?.guidance?.title || ''}
                                    placeholder="Student-first guidance"
                                    onChange={(e) => updateField(['counsellingSection', 'guidance', 'title'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Guidance Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.counsellingSection?.guidance?.desc || ''}
                                    placeholder="Your details are only used for your education enquiry."
                                    onChange={(e) => updateField(['counsellingSection', 'guidance', 'desc'], e.target.value)}
                                />
                            </Col>

                            {/* Form card settings */}
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Form Badge</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.counsellingSection?.form?.badgeText || ''}
                                    placeholder="Free"
                                    onChange={(e) => updateField(['counsellingSection', 'form', 'badgeText'], e.target.value)}
                                />
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Label className="generic-form-label">Form Button Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.counsellingSection?.form?.buttonText || ''}
                                    placeholder="Find Best University"
                                    onChange={(e) => updateField(['counsellingSection', 'form', 'buttonText'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Form Heading Parts</Form.Label>
                                <Row>
                                    <Col md={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Compare & Apply"
                                            value={data.counsellingSection?.form?.titleLine1 || ''}
                                            onChange={(e) => updateField(['counsellingSection', 'form', 'titleLine1'], e.target.value)}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="100+"
                                            value={data.counsellingSection?.form?.titleHighlight || ''}
                                            onChange={(e) => updateField(['counsellingSection', 'form', 'titleHighlight'], e.target.value)}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Online MBA..."
                                            value={data.counsellingSection?.form?.titleLine2 || ''}
                                            onChange={(e) => updateField(['counsellingSection', 'form', 'titleLine2'], e.target.value)}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 5. FAQs Section */}
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-light fw-bold">5. FAQ Section</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Badge</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.faqSection?.badge || ''}
                                    placeholder="FAQs"
                                    onChange={(e) => updateField(['faqSection', 'badge'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Title Line 1</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.faqSection?.titleLine1 || ''}
                                    placeholder="Frequently"
                                    onChange={(e) => updateField(['faqSection', 'titleLine1'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Title Line 2</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.faqSection?.titleLine2 || ''}
                                    placeholder="asked questions"
                                    onChange={(e) => updateField(['faqSection', 'titleLine2'], e.target.value)}
                                />
                            </Col>

                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Counsellor Card Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.faqSection?.counsellorCard?.text || ''}
                                    placeholder="Still unsure? Talk to a counsellor"
                                    onChange={(e) => updateField(['faqSection', 'counsellorCard', 'text'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Counsellor Card Button</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.faqSection?.counsellorCard?.buttonText || ''}
                                    placeholder="Get free counselling"
                                    onChange={(e) => updateField(['faqSection', 'counsellorCard', 'buttonText'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Counsellor Card Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.faqSection?.counsellorCard?.buttonLink || ''}
                                    placeholder="/#free-counselling"
                                    onChange={(e) => updateField(['faqSection', 'counsellorCard', 'buttonLink'], e.target.value)}
                                />
                            </Col>

                            {/* FAQ items */}
                            <Col md={12} className="mb-3 mt-2">
                                <Form.Label className="generic-form-label fw-bold">Questions & Answers</Form.Label>
                                {(data.faqSection?.faqs || []).map((faq: any, index: number) => (
                                    <div key={index} className="p-3 mb-2 border rounded bg-light">
                                        <Row>
                                            <Col md={11}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label className="small text-muted mb-1">Question ({index + 1})</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={faq.question || ''}
                                                        onChange={(e) => {
                                                            const faqs = [...(data.faqSection?.faqs || [])];
                                                            faqs[index].question = e.target.value;
                                                            updateField(['faqSection', 'faqs'], faqs);
                                                        }}
                                                    />
                                                </Form.Group>
                                                <Form.Group>
                                                    <Form.Label className="small text-muted mb-1">Answer</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={faq.answer || ''}
                                                        onChange={(e) => {
                                                            const faqs = [...(data.faqSection?.faqs || [])];
                                                            faqs[index].answer = e.target.value;
                                                            updateField(['faqSection', 'faqs'], faqs);
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={1} className="d-flex align-items-center justify-content-end">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        const faqs = [...(data.faqSection?.faqs || [])];
                                                        faqs.splice(index, 1);
                                                        updateField(['faqSection', 'faqs'], faqs);
                                                    }}
                                                >
                                                    ✕
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        const faqs = [...(data.faqSection?.faqs || [])];
                                        faqs.push({ id: faqs.length + 1, question: '', answer: '' });
                                        updateField(['faqSection', 'faqs'], faqs);
                                    }}
                                >
                                    + Add FAQ
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 6. Call To Action (Bottom) */}
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-light fw-bold">6. Bottom Call To Action</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Logo Upload</Form.Label>
                                <FileUpload
                                    fieldKey="ctaLogo"
                                    value={data.callToAction?.logo || ''}
                                    onChange={(_key: string, val: string) => updateField(['callToAction', 'logo'], val)}
                                />
                                <Form.Control
                                    type="text"
                                    className="mt-2"
                                    placeholder="Or enter logo URL manually..."
                                    value={data.callToAction?.logo || ''}
                                    onChange={(e) => updateField(['callToAction', 'logo'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Logo Alt</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.callToAction?.logoAlt || ''}
                                    placeholder="My Online University"
                                    onChange={(e) => updateField(['callToAction', 'logoAlt'], e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="mb-3">
                                <Form.Label className="generic-form-label">Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.callToAction?.title || ''}
                                    placeholder="India's Top Universities"
                                    onChange={(e) => updateField(['callToAction', 'title'], e.target.value)}
                                />
                            </Col>
                            <Col md={12} className="mb-3">
                                <Form.Label className="generic-form-label">Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={data.callToAction?.description || ''}
                                    onChange={(e) => updateField(['callToAction', 'description'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Button Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.callToAction?.buttonText || ''}
                                    placeholder="Explore programmes"
                                    onChange={(e) => updateField(['callToAction', 'buttonText'], e.target.value)}
                                />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="generic-form-label">Button Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.callToAction?.buttonLink || ''}
                                    placeholder="/#free-counselling"
                                    onChange={(e) => updateField(['callToAction', 'buttonLink'], e.target.value)}
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* 7. SEO Settings */}
                <SeoFormSection
                    data={data}
                    onChange={(field, val) => setData((prev: any) => ({ ...prev, [field]: val }))}
                />

                <Col md={12} className="mb-5">
                    <Button type="submit" className="professional-submit-btn px-5 py-2">
                        Save Home Page Content
                    </Button>
                </Col>
            </Form>
        </Container>
    );
};
